/**
 * Create / edit dialog for a single rule (#118).
 *
 * Two modes:
 *   - `mode='create'`: blank form with scope (generic|customer) toggle and
 *     a tenant dropdown for both scopes. Submits via
 *     `useCreateRule`.
 *   - `mode='edit'`: pre-fills title/body/active from the loaded rule and
 *     locks scope/customerId (the API does not move a rule between scopes).
 *     Submits via `useUpdateRule`.
 *
 * The dialog also hosts a "Versionen"-Tab (#119) and a "Testlauf"-Trigger
 * (#56) so the operator can iterate without leaving the surface.
 *
 * Uses `react-hook-form` + `zodResolver` against a form-local schema that
 * mirrors `CreateRuleRequestSchema` plus the cross-field invariant the API
 * enforces: both scopes require a tenant/customer id.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import type { CreateRuleRequest, RuleScope, UpdateRuleRequest } from '@invoice/shared/schemas';
import { History, Loader2, Save, Trash2 } from 'lucide-react';
import { type ReactNode, useEffect, useId, useMemo, useState } from 'react';
import { Controller, type SubmitHandler, type UseFormReturn, useForm } from 'react-hook-form';
import { z } from 'zod';

import { MARKDOWN_MAX_LENGTH } from '@/components/MarkdownEditor';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { CustomerLookupField } from '@/components/CustomerLookupField';
import { SupplierLookupField } from '@/components/SupplierLookupField';
import { RuleTestRunDialog } from '@/components/RuleTestRunDialog';
import { RuleVersionsPanel } from '@/components/RuleVersionsPanel';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useI18n } from '@/lib/i18n';
import { useCreateRule, useDeleteRule, useUpdateRule } from '@/lib/queries/rules';
import { cn } from '@/lib/utils';

/** Form-local schema; mirrors `CreateRuleRequestSchema` from shared. */
function createRuleFormSchema(t: ReturnType<typeof useI18n>['t']) {
  return z
    .object({
      scope: z.enum(['generic', 'customer']),
      customerId: z.string().optional(),
      customerName: z.string().optional(),
      supplierName: z.string().optional(),
      title: z
        .string()
        .min(1, t('ruleDialog.titleRequired'))
        .max(200, t('ruleDialog.titleMax')),
      body: z
        .string()
        .min(1, t('ruleDialog.bodyRequired'))
        .max(MARKDOWN_MAX_LENGTH, t('ruleDialog.bodyMax', { max: MARKDOWN_MAX_LENGTH })),
      active: z.boolean(),
    })
    .superRefine((value, ctx) => {
      if (!value.customerId || value.customerId.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['customerId'],
          message: t('ruleDialog.tenantRequired'),
        });
      }
      if (
        value.scope === 'customer' &&
        (!value.supplierName || value.supplierName.trim().length === 0)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['supplierName'],
          message: t('ruleDialog.supplierRequired'),
        });
      }
    });
}
type RuleFormValues = z.infer<ReturnType<typeof createRuleFormSchema>>;

/** Existing-rule view loaded by the parent for edit-mode. */
export interface ExistingRule {
  id: string;
  scope: RuleScope;
  customerId: string | null;
  customerName?: string | null;
  supplierName?: string | null;
  title: string;
  body: string;
  active: boolean;
  version: number;
}

type RuleEditDialogProps =
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      mode: 'create';
      defaultScope: RuleScope;
      defaultCustomerId?: string | null;
      defaultCustomerName?: string | null;
      onSuccess?: () => void;
      rule?: never;
    }
  | {
      open: boolean;
      onOpenChange: (open: boolean) => void;
      mode: 'edit';
      rule: ExistingRule;
      onSuccess?: () => void;
      defaultScope?: never;
      defaultCustomerId?: never;
      defaultCustomerName?: never;
    };

type Tab = 'edit' | 'versions';

export function RuleEditDialog(props: RuleEditDialogProps) {
  const { t } = useI18n();
  const titleId = useId();
  const titleFieldId = useId();
  const bodyFieldId = useId();
  const customerFieldId = useId();
  const supplierFieldId = useId();
  const [tab, setTab] = useState<Tab>('edit');
  const [testRunOpen, setTestRunOpen] = useState(false);
  const [selectedSupplierLookupId, setSelectedSupplierLookupId] = useState<string | null>(null);

  const isEdit = props.mode === 'edit';

  // Stable default values: depend on the *primitive* rule fields rather than
  // the props object. Otherwise every parent render hands us a new
  // `props` reference, which would re-fire the useEffect-reset below and
  // create a render-loop.
  const defaultScopeFromProps = isEdit ? props.rule.scope : props.defaultScope;
  const defaultCustomerIdFromProps = isEdit
    ? (props.rule.customerId ?? undefined)
    : (props.defaultCustomerId ?? undefined);
  const defaultCustomerNameFromProps = isEdit
    ? (props.rule.customerName ?? undefined)
    : (props.defaultCustomerName ?? undefined);
  const defaultSupplierNameFromProps = isEdit ? (props.rule.supplierName ?? undefined) : undefined;
  const initialTitle = isEdit ? props.rule.title : '';
  const initialBody = isEdit ? props.rule.body : '';
  const initialActive = isEdit ? props.rule.active : true;

  const defaults: RuleFormValues = useMemo(
    () => ({
      scope: defaultScopeFromProps,
      customerId: defaultCustomerIdFromProps,
      customerName: defaultCustomerNameFromProps,
      supplierName: defaultSupplierNameFromProps,
      title: initialTitle,
      body: initialBody,
      active: initialActive,
    }),
    [
      defaultScopeFromProps,
      defaultCustomerIdFromProps,
      defaultCustomerNameFromProps,
      defaultSupplierNameFromProps,
      initialTitle,
      initialBody,
      initialActive,
    ],
  );
  const formSchema = useMemo(() => createRuleFormSchema(t), [t]);

  const form = useForm<RuleFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaults,
    mode: 'onBlur',
  });

  // Reset the form when the dialog opens fresh (different rule id, etc.).
  useEffect(() => {
    if (props.open) {
      form.reset(defaults);
      setTab('edit');
      setSelectedSupplierLookupId(null);
    }
  }, [props.open, defaults, form]);

  const createMutation = useCreateRule();
  const updateMutation = useUpdateRule();
  const deleteMutation = useDeleteRule();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const submitting =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const handleSubmit: SubmitHandler<RuleFormValues> = async (values) => {
    if (props.mode === 'create') {
      await createMutation.mutateAsync(buildCreatePayload(values));
    } else {
      await updateMutation.mutateAsync({ id: props.rule.id, patch: buildUpdatePatch(values) });
    }
    props.onSuccess?.();
    props.onOpenChange(false);
  };

  const handleDelete = async () => {
    if (props.mode !== 'edit') return;
    await deleteMutation.mutateAsync(props.rule.id);
    props.onSuccess?.();
    setDeleteConfirmOpen(false);
    props.onOpenChange(false);
  };

  const scope = form.watch('scope');
  const titleValue = form.watch('title');
  const bodyValue = form.watch('body');
  const customerIdValue = form.watch('customerId');
  const customerNameValue = form.watch('customerName');
  const supplierNameValue = form.watch('supplierName');

  // Draft used for the test-run dialog. Built from the live form values so
  // an iteratively-edited rule is testable without saving.
  const draft = useMemo(
    () =>
      titleValue.length > 0 && bodyValue.length > 0
        ? {
            scope,
            customerId: customerIdValue ?? null,
            supplierName: supplierNameValue ?? null,
            title: titleValue,
            body: bodyValue,
          }
        : null,
    [scope, customerIdValue, supplierNameValue, titleValue, bodyValue],
  );

  const submitError = createMutation.error ?? updateMutation.error ?? deleteMutation.error;
  const headerTitle = isEdit
    ? t('ruleDialog.headerEdit', { version: props.rule.version })
    : t('ruleDialog.headerCreate');
  const headerDescription = isEdit
    ? t('ruleDialog.descriptionEdit')
    : t('ruleDialog.descriptionCreate');
  const showVersionsTab = tab === 'versions' && isEdit;
  const versionsRuleId = isEdit ? props.rule.id : null;

  const handleClose = () => props.onOpenChange(false);
  const handleRollback = () => {
    props.onSuccess?.();
    props.onOpenChange(false);
  };

  return (
    <>
      <Dialog open={props.open} onOpenChange={props.onOpenChange} ariaLabelledBy={titleId}>
        <DialogHeader className="flex-row items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <DialogTitle id={titleId}>{headerTitle}</DialogTitle>
            <DialogDescription>{headerDescription}</DialogDescription>
          </div>
          <DialogCloseButton onClose={handleClose} />
        </DialogHeader>

        {isEdit ? <RuleTabsBar tab={tab} onChange={setTab} /> : null}

        <DialogBody>
          {showVersionsTab && versionsRuleId ? (
            <RuleVersionsPanel ruleId={versionsRuleId} onRolledBack={handleRollback} />
          ) : (
            <RuleEditForm
              form={form}
              submitting={submitting}
              isEdit={isEdit}
              customerName={customerNameValue}
              supplierName={supplierNameValue}
              titleFieldId={titleFieldId}
              bodyFieldId={bodyFieldId}
              customerFieldId={customerFieldId}
              supplierFieldId={supplierFieldId}
              selectedSupplierLookupId={selectedSupplierLookupId}
              onSupplierLookupChange={setSelectedSupplierLookupId}
              submitError={submitError}
              onSubmit={handleSubmit}
            />
          )}
        </DialogBody>

        <RuleEditFooter
          isEdit={isEdit}
          tab={tab}
          submitting={submitting}
          draftAvailable={draft !== null}
          onCancel={handleClose}
          onTestRun={() => setTestRunOpen(true)}
          onDelete={() => {
            setDeleteConfirmOpen(true);
          }}
        />
      </Dialog>

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={t('ruleDialog.deleteTitle')}
        description={t('ruleDialog.deleteDescription')}
        confirmLabel={t('ruleDialog.delete')}
        cancelLabel={t('ruleDialog.cancel')}
        confirmVariant="destructive"
        pending={deleteMutation.isPending}
        onConfirm={() => {
          void handleDelete();
        }}
      />

      {/* Mount the test-run dialog only when needed so the underlying invoice
       * list query does not run on every edit-dialog open. */}
      {testRunOpen ? (
        <RuleTestRunDialog open={testRunOpen} onOpenChange={setTestRunOpen} draft={draft} />
      ) : null}
    </>
  );
}

function buildCreatePayload(values: RuleFormValues): CreateRuleRequest {
  return {
    scope: values.scope,
    customerId: values.customerId ?? null,
    supplierName: values.scope === 'customer' ? (values.supplierName?.trim() ?? null) : null,
    title: values.title,
    body: values.body,
    active: values.active,
  };
}

function buildUpdatePatch(values: RuleFormValues): UpdateRuleRequest {
  return {
    title: values.title,
    body: values.body,
    active: values.active,
  };
}

interface RuleTabsBarProps {
  tab: Tab;
  onChange: (next: Tab) => void;
}

function RuleTabsBar({ tab, onChange }: RuleTabsBarProps) {
  const { t } = useI18n();
  return (
    <div className="border-b border-border bg-muted/20 px-6 py-2" role="tablist">
      <TabButton active={tab === 'edit'} onClick={() => onChange('edit')} testId="rule-tab-edit">
        {t('ruleDialog.tabEdit')}
      </TabButton>
      <TabButton
        active={tab === 'versions'}
        onClick={() => onChange('versions')}
        testId="rule-tab-versions"
      >
        <History className="size-3.5" aria-hidden />
        {t('ruleDialog.tabVersions')}
      </TabButton>
    </div>
  );
}

interface RuleEditFooterProps {
  isEdit: boolean;
  tab: Tab;
  submitting: boolean;
  draftAvailable: boolean;
  onCancel: () => void;
  onTestRun: () => void;
  onDelete: () => void;
}

function RuleEditFooter({
  isEdit,
  tab,
  submitting,
  draftAvailable,
  onCancel,
  onTestRun,
  onDelete,
}: RuleEditFooterProps) {
  const { t } = useI18n();
  return (
    <DialogFooter className="sm:items-center sm:justify-between">
      <div className="flex items-center gap-2">
        {isEdit ? (
          <Button
            type="button"
            variant="outline"
            onClick={onDelete}
            disabled={submitting}
            aria-label={t('ruleDialog.delete')}
            data-testid="rule-edit-delete"
          >
            <Trash2 className="size-4" aria-hidden />
            {t('ruleDialog.delete')}
          </Button>
        ) : null}
        <Button
          type="button"
          variant="outline"
          disabled={!draftAvailable || submitting}
          onClick={onTestRun}
          data-testid="rule-edit-test-run-trigger"
        >
          {t('ruleDialog.testRun')}
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          {t('ruleDialog.cancel')}
        </Button>
        {tab === 'edit' ? (
          <Button
            type="submit"
            form="rule-edit-form"
            disabled={submitting}
            data-testid="rule-edit-submit"
          >
            {submitting ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Save className="size-4" aria-hidden />
            )}
            {isEdit ? t('ruleDialog.save') : t('ruleDialog.create')}
          </Button>
        ) : null}
      </div>
    </DialogFooter>
  );
}

interface RuleEditFormProps {
  form: UseFormReturn<RuleFormValues>;
  submitting: boolean;
  isEdit: boolean;
  customerName?: string | undefined;
  supplierName?: string | undefined;
  titleFieldId: string;
  bodyFieldId: string;
  customerFieldId: string;
  supplierFieldId: string;
  selectedSupplierLookupId: string | null;
  onSupplierLookupChange: (next: string | null) => void;
  submitError: Error | null;
  onSubmit: SubmitHandler<RuleFormValues>;
}

function RuleEditForm({
  form,
  submitting,
  isEdit,
  customerName,
  supplierName,
  titleFieldId,
  bodyFieldId,
  customerFieldId,
  supplierFieldId,
  selectedSupplierLookupId,
  onSupplierLookupChange,
  submitError,
  onSubmit,
}: RuleEditFormProps) {
  const { t } = useI18n();
  return (
    <form
      id="rule-edit-form"
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-4"
      noValidate
      data-testid="rule-edit-form"
    >
      <fieldset disabled={submitting} className="flex flex-col gap-4">
        {!isEdit ? (
          <div className="flex flex-col gap-2">
            <Label>{t('ruleDialog.scopeLabel')}</Label>
            <Controller
              control={form.control}
              name="scope"
              render={({ field }) => (
                <div className="flex flex-wrap gap-2" role="radiogroup">
                  <ScopeRadio
                    value="generic"
                    current={field.value}
                    onSelect={(next) => {
                      field.onChange(next);
                      if (next === 'generic') {
                        form.setValue('supplierName', undefined);
                        onSupplierLookupChange(null);
                      }
                    }}
                    label={t('ruleDialog.scopeGeneric')}
                  />
                  <ScopeRadio
                    value="customer"
                    current={field.value}
                    onSelect={field.onChange}
                    label={t('ruleDialog.scopeCustomer')}
                  />
                </div>
              )}
            />
          </div>
        ) : null}

        <Controller
          control={form.control}
          name="customerId"
          render={({ field }) => (
            <CustomerLookupField
              label={t('rules.tenantLabel')}
              inputId={customerFieldId}
              placeholder={t('rules.tenantPlaceholder')}
              idleHint={t('rules.tenantMinSearch')}
              loadingText={t('rules.tenantLoading')}
              emptyText={t('rules.tenantEmpty')}
              selectedPrefix={t('common.selected')}
              clearAriaLabel={`${t('rules.tenantLabel')} ${t('common.clear')}`}
              selectedCustomerId={field.value}
              selectedCustomerName={customerName}
              onSelect={(customer) => {
                field.onChange(customer?.id);
                form.setValue('customerName', customer?.name);
                form.setValue('supplierName', undefined);
                onSupplierLookupChange(null);
              }}
              disabled={isEdit}
              required
              errorMessage={form.formState.errors.customerId?.message}
              inputTestId="rule-edit-customer-search"
              resultsTestId="rule-edit-customer-results"
            />
          )}
        />

        {form.watch('scope') === 'customer' ? (
          <SupplierLookupField
            customerId={form.watch('customerId') ?? null}
            label={t('ruleDialog.supplierLabel')}
            inputId={supplierFieldId}
            placeholder={t('ruleDialog.supplierPlaceholder')}
            idleHint={t('ruleDialog.supplierIdleHint')}
            loadingText={t('ruleDialog.supplierLoading')}
            emptyText={t('ruleDialog.supplierEmpty')}
            selectedPrefix={t('common.selected')}
            clearAriaLabel={t('ruleDialog.supplierClearAria')}
            selectedSupplierId={selectedSupplierLookupId}
            selectedSupplierName={supplierName}
            onSelect={(supplier) => {
              form.setValue('supplierName', supplier?.name);
              onSupplierLookupChange(supplier?.id ?? null);
            }}
            disabled={isEdit || !form.watch('customerId')}
            required
            errorMessage={form.formState.errors.supplierName?.message}
            inputTestId="rule-edit-supplier"
            resultsTestId="rule-edit-supplier-results"
          />
        ) : null}

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={titleFieldId}>{t('ruleDialog.titleLabel')}</Label>
          <Input
            id={titleFieldId}
            {...form.register('title')}
            aria-invalid={form.formState.errors.title ? true : undefined}
            data-testid="rule-edit-title"
          />
          {form.formState.errors.title ? (
            <p className="text-xs text-destructive" role="alert">
              {form.formState.errors.title.message}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor={bodyFieldId}>{t('ruleDialog.bodyLabel')}</Label>
          <Controller
            control={form.control}
            name="body"
            render={({ field }) => (
              <>
                <textarea
                  id={bodyFieldId}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  name={field.name}
                  ref={field.ref}
                  rows={14}
                  maxLength={MARKDOWN_MAX_LENGTH}
                  aria-label={t('ruleDialog.bodyAria')}
                  aria-invalid={form.formState.errors.body ? true : undefined}
                  data-testid="rule-edit-body"
                  className={cn(
                    'min-h-64 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm outline-none',
                    'placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                />
                <p className="text-xs text-muted-foreground">
                  {t('ruleDialog.characters', {
                    count: field.value.length,
                    max: MARKDOWN_MAX_LENGTH,
                  })}
                </p>
              </>
            )}
          />
          {form.formState.errors.body ? (
            <p className="text-xs text-destructive" role="alert">
              {form.formState.errors.body.message}
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <input
            id="rule-edit-active"
            type="checkbox"
            {...form.register('active')}
            className="size-4 rounded border-input"
            data-testid="rule-edit-active"
          />
          <Label htmlFor="rule-edit-active">{t('ruleDialog.activeLabel')}</Label>
        </div>
      </fieldset>

      {submitError ? (
        <div
          role="alert"
          data-testid="rule-edit-error"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {submitError.message}
        </div>
      ) : null}
    </form>
  );
}

interface ScopeRadioProps {
  value: RuleScope;
  current: RuleScope;
  onSelect: (next: RuleScope) => void;
  label: string;
}

function ScopeRadio({ value, current, onSelect, label }: ScopeRadioProps) {
  const active = value === current;
  const id = `rule-edit-scope-${value}`;
  return (
    <label
      htmlFor={id}
      className={cn(
        'inline-flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
        active
          ? 'border-accent bg-accent/10 text-foreground'
          : 'border-border bg-background text-muted-foreground hover:border-accent/40',
      )}
    >
      <input
        id={id}
        type="radio"
        name="rule-edit-scope"
        value={value}
        checked={active}
        onChange={() => onSelect(value)}
        data-testid={id}
        className="size-4"
      />
      {label}
    </label>
  );
}

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: ReactNode;
}

function TabButton({ active, onClick, testId, children }: TabButtonProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'mr-1 inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:bg-background/60',
      )}
    >
      {children}
    </button>
  );
}
